import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Modal as QcsModal } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { QcsModalBox } from '../common/basic/QcsModalBox';
import { SectionHeader } from '../common/SectionHeader';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsTypography } from '../common/basic/QcsTypography';
import QcsAlert from '../common/basic/QcsAlert';
import { CopyableBlock } from './CopyableBlock';
import { IntegrationConnectionDetails } from './IntegrationConnectionDetails';
import { downloadCredentialsFile, IntegrationCredentials } from './integrationUtils';

interface Props {
  credentials: IntegrationCredentials;
  onClose: () => void;
}

/**
 * What to send to the partner after a key was registered. A new API key exists only in memory,
 * so the modal can't be closed - nor the page left - until the key was copied or the credentials file downloaded.
 */
export const IntegrationCredentialsModal: FC<Props> = ({ credentials, onClose }) => {
  const { t } = useTranslation();
  const [downloaded, setDownloaded] = useState(false);
  const [copied, setCopied] = useState(false);

  const mustSecure = !!credentials.apiKey && !downloaded && !copied;

  useEffect(() => {
    if (!mustSecure) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [mustSecure]);

  const handleDownload = () => {
    downloadCredentialsFile(credentials);
    setDownloaded(true);
  };

  const renderContent = () => {
    if (credentials.apiKey) {
      return (
        <>
          <QcsTypography variant="body2">{t('integration.apiAccess.credentials.apiKeyDescription')}</QcsTypography>
          <CopyableBlock text={credentials.apiKey} onCopy={() => setCopied(true)} />
          <QcsAlert severity="warning">{t('integration.apiAccess.credentials.apiKeyWarning')}</QcsAlert>
          <QcsTypography variant="body2">{t('integration.apiAccess.credentials.sendDescription')}</QcsTypography>
          <IntegrationConnectionDetails details={credentials} copyAll />
          <QcsButton variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} disabled={downloaded} sx={{ alignSelf: 'flex-start' }}>
            {t('integration.apiAccess.credentials.download')}
          </QcsButton>
          <QcsTypography variant="body2">{t('integration.apiAccess.credentials.apiKeyNotRecoverable')}</QcsTypography>
        </>
      );
    }
    return (
      <>
        <QcsTypography variant="body2">{t('integration.apiAccess.credentials.sendDescription')}</QcsTypography>
        <IntegrationConnectionDetails details={credentials} copyAll />
      </>
    );
  };

  return (
    <QcsModal open onClose={mustSecure ? undefined : onClose}>
      <QcsModalBox>
        <SectionHeader title="integration.apiAccess.credentials.title" />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {renderContent()}
          <QcsButton variant="outlined" onClick={onClose} disabled={mustSecure} sx={{ alignSelf: 'flex-end' }}>
            {t('common.close')}
          </QcsButton>
        </Box>
      </QcsModalBox>
    </QcsModal>
  );
};
